import * as Sentry from '@sentry/react';
// import { BrowserTracing } from '@sentry/tracing';
import moment from 'moment';
import Utils from 'utils';
// 公共样式
import Config from 'config/Config';

// 错误缓存和频率控制
let errorCache = new Map(); // 存储错误的缓存
let pendingErrors = []; // 待上报的错误队列
let isDevEnvironment = process.env.NODE_ENV === 'development'; // 是否为开发环境
let isUat = [
  'h5.uat3.yofoys.cn',
  'h5.uat5.yofoys.cn',
  'h5.test.yofoys.cn',
  'h5.test2.yofoys.cn',
  'h5.dev.yofoys.cn'
].includes(location.hostname);
// 定时器相关
let autoReportTimer = null; // 自动上报定时器
const AUTO_REPORT_INTERVAL = 30 * 1000; // 30秒自动上报间隔

// 限频上报相关
let lastThrottledReportTime = 0; // 上次限频上报的时间
const THROTTLED_REPORT_INTERVAL = 60 * 1000; // 1分钟限频间隔
const userInfo = Utils.getLocalCache(Config.localStorageKeys.member_userInfo) || {};
/**
 * sentry 初始化
 */
const init = () => {
  // 如果是开发环境，只初始化但不上报
  if (isDevEnvironment) {
    console.log('开发环境，Sentry 已禁用上报功能');
    return;
  }
  Sentry.init({
    dsn: 'https://f437a2997b2b4c67776f0399315086c5@sentry.yofogo.com/13',
    release: `${process.env.BRANCH_NAME}`,
    environment: Config.HOST || 'PROD',

    // 基础配置
    maxBreadcrumbs: 100, // 增加面包屑数量，确保更多上下文信息被捕获
    maxValueLength: 1000, // 限制值的最大长度，避免过大的数据
    normalizeDepth: 10, // 增加对象序列化深度

    // 采样率配置
    sampleRate: 1, // 确保100%的错误事件都被发送到beforeSend
    tracesSampleRate: 1, // 保持100%采样率，确保所有事务都被跟踪
    profilesSampleRate: 1.0, // 启用性能分析采样

    // Replay 配置
    replaysSessionSampleRate: 1.0, // 设置为100%，确保所有会话都被记录
    replaysOnErrorSampleRate: 1.0, // 保持100%，确保所有错误会话都被记录

    // 增强功能
    attachStacktrace: true, // 自动附加堆栈跟踪到所有消息
    autoSessionTracking: false, // 自动会话跟踪
    sendDefaultPii: true, // 不发送个人身份信息，保护隐私
    clientReports: true, // 启用客户端报告
    enableTracing: true, // 启用链路追踪

    // 初始作用域配置
    initialScope: {
      tags: {
        component: 'h5-app',
        version: process.env.REACT_APP_VERSION || '1.0.0',
        platform: 'web',
        userAgent: navigator.userAgent,
        cardCode: userInfo.cardCode || '',
        realName: userInfo.realName || '',
        shopNo: userInfo.shopNo || '',
        userType: userInfo.userType || ''
      },
      level: 'error'
    },
    integrations: [
      Sentry.captureConsoleIntegration({ levels: ['error', 'warning'] }),
      // 基础集成
      // Sentry.dedupeIntegration(), // 显式添加去重集成
      Sentry.browserTracingIntegration({
        // 路由变化追踪 (如果使用 React Router)
        enableLongTask: true, // 启用长任务监控
        enableInp: true, // 启用交互到下一次绘制监控
        enableUserTimingAPI: true // 启用用户计时API
      }),
      Sentry.browserProfilingIntegration(),

      // 增强错误数据集成
      Sentry.extraErrorDataIntegration({
        depth: 10, // 捕获更深层的错误数据
        captureErrorCause: true // 捕获错误原因
      }),

      // 上下文行集成
      Sentry.contextLinesIntegration({
        frameContextLines: 20 // 错误堆栈上下文行数
      }),

      // HTTP 上下文集成
      Sentry.httpContextIntegration({
        breadcrumbs: true, // 启用HTTP面包屑
        tracing: true // 启用HTTP追踪
      }),
      Sentry.replayIntegration({
        // Replay 配置
        maskAllText: false, // 不遮罩所有文本
        maskAllInputs: true, // 遮罩所有输入框
        blockAllMedia: false, // 不阻止所有媒体

        // 网络监控配置
        networkDetailAllowUrls: [
          // 允许记录详细网络信息的URL模式
          /^https:\/\/api\.yourapp\.com/,
          /^https:\/\/.*\.yourapp\.com/,
          /.*/,
          'https://*',
          'http://*'
        ],
        networkCaptureBodies: true, // 捕获网络请求体
        networkRequestHeaders: [
          'User-Agent',
          'Content-Type',
          'Authorization',
          'Accept',
          'Accept-Language',
          'Cache-Control',
          'Referer',
          'X-Requested-With'
        ], // 捕获的请求头
        networkResponseHeaders: [
          'Content-Type',
          'Content-Length',
          'Cache-Control',
          'ETag',
          'Last-Modified',
          'Server',
          'reqid',
          'tid'
        ], // 捕获的响应头

        // 性能优化
        useCompression: true, // 启用压缩

        // 隐私保护
        mask: ['.sensitive', '[data-sensitive]'], // 遮罩敏感元素
        block: ['.ads', '.tracking'], // 阻止录制的元素

        // 录制时长配置
        maxReplayDuration: 5 * 60 * 1000, // 最大录制时长为5分钟

        // 采样配置
        sessionSampleRate: 1.0, // 会话采样率
        errorSampleRate: 1.0 // 错误采样率
      })
    ],
    beforeSend(event, hint) {
      if (event.extra && event.extra.type === 'js_error') {
        return event;
      }
      trackReport({ error: hint.originalException });
      // 如果是uat环境不限制上报频率
      if (isUat) {
        directReport();
      }
      return null;
    }
  });
  startAutoReport();
};

/**
 * sentry 上报信息
 * @param {Object} error 错误信息
 * @param {Object} errorInfo 组件信息
 * @param {Object} extra 附加信息
 * @param {Object} tag 标签信息 key, value
 * @param {Boolean} force 是否强制上报，忽略频率限制
 */
const trackReport = ({ error, extra = {} }) => {
  pendingErrors.push({
    event: error,
    extra: extra,
    timestamp: Date.now(),
    reported: false
  });
};

/**
 * 直接手动上报最新的一条错误（无任何限制）
 * @param {Boolean} clearCache 是否清空缓存
 * @returns {Number} 上报结果：1-成功，0-失败或无错误
 */
const directReport = (clearCache = false) => {
  if (pendingErrors.length === 0) {
    console.log('没有待上报的错误');
    return 0;
  }

  // 找到最新的未上报错误（从后往前找）
  let latestErrorIndex = -1;
  for (let i = pendingErrors.length - 1; i >= 0; i--) {
    if (!pendingErrors[i].reported) {
      latestErrorIndex = i;
      break;
    }
  }
  if (latestErrorIndex === -1) {
    console.log('没有未上报的错误');
    return 0;
  }
  const errorItem = pendingErrors[latestErrorIndex];
  let uploadSuccess = false;
  try {
    let m_error = new Error(errorItem.event);
    m_error.stack = errorItem.event.stack;
    // 在这里可根据业务情况发送用户信息
    const userInfo = Utils.getLocalCache(Config.localStorageKeys.member_userInfo) || {};

    let user = {
      user_id: userInfo.id || '',
      cardCode: userInfo.cardCode || '',
      realName: userInfo.realName || '',
      userName: userInfo.userName || '',
      shopNo: userInfo.shopNo || '',
      userType: userInfo.userType || '',
      channel: 'b2c_web_h5',
      host: Config.HOST || '',
      time: moment().format('YYYY-MM-DD HH:mm:ss')
    };

    Sentry.captureException(m_error, {
      extra: { type: 'js_error', msg: errorItem.extra && errorItem.extra.msg },
      user
    });
    // 标记为已上报
    pendingErrors[latestErrorIndex].reported = true;
    uploadSuccess = true;
  } catch (error) {
    console.error('直接上报失败:', error);
    uploadSuccess = false;
  }

  // 上传成功后只删除已上报的那一条错误记录
  if (uploadSuccess) {
    // 从pendingErrors数组中移除已上报的错误
    pendingErrors.splice(latestErrorIndex, 1);

    // 从errorCache中移除对应的错误记录
    if (errorItem.errorKey) {
      errorCache.delete(errorItem.errorKey);
    }
  }

  // 如果手动指定需要清空缓存（兼容原有逻辑）
  if (clearCache) {
    pendingErrors = [];
    errorCache.clear();
    console.log('手动清空所有错误缓存');
  }

  return uploadSuccess ? 1 : 0;
};

/**
 * 限频手动上报最新的一条错误（每分钟只能点击一次）
 * @param {Boolean} clearCache 是否清空缓存
 * @returns {Number} 上报结果：1-成功，0-失败或无错误，-1-频率限制
 */
const throttledReport = (clearCache = false) => {
  const now = Date.now();

  // 检查是否在限频时间内
  if (now - lastThrottledReportTime < THROTTLED_REPORT_INTERVAL) {
    const remainingTime = Math.ceil(
      (THROTTLED_REPORT_INTERVAL - (now - lastThrottledReportTime)) / 1000
    );
    console.log(`限频上报：请等待 ${remainingTime} 秒后再试`);
    return -1;
  }

  // 更新上次上报时间
  lastThrottledReportTime = now;

  // 调用直接上报函数
  const result = directReport(clearCache);

  return result;
};

/**
 * 手动上报最新的一条错误（保持向后兼容，默认使用限频上报）
 * @param {Boolean} clearCache 是否清空缓存
 * @returns {Number} 上报结果：1-成功，0-失败或无错误，-1-频率限制
 */
const manualReport = (clearCache = false) => {
  // 为了保持向后兼容，默认使用限频上报
  return throttledReport(clearCache);
};

/**
 * 获取待上报错误的统计信息
 * @returns {Object} 错误统计信息
 */
const getPendingErrorsInfo = () => {
  const unreportedCount = pendingErrors.filter((item) => !item.reported).length;
  return {
    total: pendingErrors.length,
    unreported: unreportedCount,
    reported: pendingErrors.length - unreportedCount
  };
};

/**
 * 启动自动上报定时器
 * 每5分钟自动执行一次错误上报
 */
const startAutoReport = () => {
  // 如果定时器已存在，先清除
  if (autoReportTimer) {
    clearInterval(autoReportTimer);
  }

  autoReportTimer = setInterval(() => {
    console.log('触发自动上传日志');
    const result = directReport(); // 定时器使用直接上报，不受限频限制
  }, AUTO_REPORT_INTERVAL);
};

/**
 * 停止自动上报定时器
 */
const stopAutoReport = () => {
  if (autoReportTimer) {
    clearInterval(autoReportTimer);
    autoReportTimer = null;
    console.log('已停止自动上报定时器');
  }
};

/**
 * 重启自动上报定时器
 */
const restartAutoReport = () => {
  stopAutoReport();
  startAutoReport();
};

// 设置全局sentry
window.sentry = {
  ...Sentry,
  init,
  trackReport,
  manualReport,
  directReport,
  throttledReport,
  getPendingErrorsInfo,
  startAutoReport,
  stopAutoReport,
  restartAutoReport
};

export {
  init,
  trackReport,
  manualReport,
  directReport,
  throttledReport,
  getPendingErrorsInfo,
  startAutoReport,
  stopAutoReport,
  restartAutoReport
};



// WEBPACK FOOTER //
// ./src/utils/sentry.js